import type { BaseFormComposerProps } from './types';

export const TRIGGER_3DS_SESSION_ERROR = '3DS Challenge Required';

const CREDIT_CARD_NUMBER_ATTRIBUTE = '__creditCardNumber__';
const CREDIT_CARD_CVV_ATTRIBUTE = '__creditCardCvv__';
const CREDIT_CARD_TYPE_ATTRIBUTE = '__creditCardType__';
const CREDIT_CARD_EXPIRATION_MONTH_ATTRIBUTE = '__creditCardExpirationMonth__';
const CREDIT_CARD_EXPIRATION_YEAR_ATTRIBUTE = '__creditCardExpirationYear__';
const CREDIT_CARD_TOKEN_ATTRIBUTE = '__creditCardToken__';

export const CREDIT_CARD_ATTRIBUTES = {
  number: CREDIT_CARD_NUMBER_ATTRIBUTE,
  cvv: CREDIT_CARD_CVV_ATTRIBUTE,
  type: CREDIT_CARD_TYPE_ATTRIBUTE,
  expirationMonth: CREDIT_CARD_EXPIRATION_MONTH_ATTRIBUTE,
  expirationYear: CREDIT_CARD_EXPIRATION_YEAR_ATTRIBUTE,
  token: CREDIT_CARD_TOKEN_ATTRIBUTE,
} as const;

export type SafeCreditCardAttributes = Exclude<
  (typeof CREDIT_CARD_ATTRIBUTES)[keyof typeof CREDIT_CARD_ATTRIBUTES],
  typeof CREDIT_CARD_CVV_ATTRIBUTE | typeof CREDIT_CARD_NUMBER_ATTRIBUTE
>;

export type CreditCardProvider = 'apple' | 'external' | 'spreedly' | 'stripe';

export interface CreditCardPlaceholders {
  readonly cardNumberPlaceholder?: string;
  readonly expirationPlaceholder?: string;
  readonly cvvPlaceholder?: string;
}
export interface CreditCardDetailsProps extends CreditCardPlaceholders {
  readonly prefilledCard?: {
    readonly number: string;
    readonly cvv: string;
    readonly expiration: string;
  };
}

export interface CreditCardProps<TType extends CreditCardProvider = CreditCardProvider> {
  readonly provider: TType;
}

export interface ExternalRequestParams {
  readonly url: string;
  readonly headers?: Record<string, string>;
  readonly payloadMap: Record<string, string>;

  readonly responseMap: {
    readonly token: string;
    readonly error: string;
  };
}

export interface ExternalCreditCardProps extends CreditCardProps<'external'>, CreditCardDetailsProps {
  readonly tokenization: ExternalRequestParams;
  readonly valueMap: ValueMap;
}
export interface ValueMap {
  readonly token: `{${(typeof CREDIT_CARD_ATTRIBUTES)['token']}}`;
  readonly [key: string]: string;
}

type ApplePayMerchantCapability = 'supports3DS' | 'supportsCredit' | 'supportsDebit' | 'supportsEMV';

export interface ApplePayProps extends CreditCardProps<'apple'> {
  readonly supportedNetworks: string[];
  readonly merchantCapabilities: ApplePayMerchantCapability[];
}

export interface SpreedlyCreditCardProps<TAttribute extends string = string>
  extends CreditCardDetailsProps,
    CreditCardProps<'spreedly'> {
  /** Data passed under this property is to be generated by the front-end components (e.g. browser_info) */
  readonly threeDSDetailsAttribute?: TAttribute;
  /** Data passed under this property is to be received from back-end and passed back as is (e.g. paymentMethodToActivate)*/
  readonly activationDetailsAttribute?: TAttribute;
  readonly showThreeDS?: boolean;
}

export type FormCreditCardComposerConfigurableProps<TAttribute extends string = string> =
  | ApplePayProps
  | ExternalCreditCardProps
  | SpreedlyCreditCardProps<TAttribute>;

export type FormCreditCardComposerProps = BaseFormComposerProps & FormCreditCardComposerConfigurableProps;
